import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaReceipt, FaMoneyBillWave, FaWater, FaTrash, FaHome, FaCalculator } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const BillsPage = () => {
    const [bills, setBills] = useState(null); // Use null initially to check if data has been fetched
    const [loading, setLoading] = useState(false); // Used for fetching data
    const [userDetails, setUserDetails] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        // Automatically fetch bills if userDetails is set (e.g., if a detail was persisted or passed)
        if (userDetails) {
            fetchBills();
        }
    }, [userDetails]);

    const fetchBills = async () => {
        if (!userDetails.trim()) {
            toast.error("Please enter a User Detail (e.g., ID or email) to fetch bills.");
            setBills(null); // Clear previous bills if search is empty
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/bill/view-bill/${userDetails}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            // Check if response has valid bill data structure
            if (response.data.response && typeof response.data.response === 'object' && response.data.response !== null) {
                 setBills(response.data.response);
                 toast.success(`Bills fetched successfully for: ${userDetails}`);
            } else {
                 setBills(null); // Set to null if no valid data
                 toast.info(`No bills found for: ${userDetails}`);
            }

        } catch (error) {
            console.error("Error fetching bills:", error);
            setBills(null);
            toast.error(error.response?.data?.response || "Failed to fetch bills. User details might be invalid or no bills exist.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setUserDetails(e.target.value);
    };

    const handleSearchClick = () => {
        fetchBills();
    };

    const handleEnterKeyPress = (e) => {
        if (e.key === "Enter") {
            fetchBills();
        }
    };
    
    // Helper function to format currency
    const formatCurrency = (amount) => {
        const value = parseFloat(amount);
        return isNaN(value) ? 'N/A' : `Ksh ${value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    
    // UI mapping for bill types
    const billItems = bills ? [
        { label: "Water Bill", value: bills.WaterBill, icon: FaWater, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Trash Bill", value: bills.Trashbill, icon: FaTrash, color: "text-green-600", bg: "bg-green-50" },
        { label: "Rent Bill", value: bills.RentBill, icon: FaHome, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Service Bill", value: bills.ServiceFee, icon: FaHome, color: "text-green-600", bg: "bg-purple-50" },
    ] : [];

    const totalBillAmount = bills?.TotalAmount ? formatCurrency(bills.TotalAmount) : formatCurrency(0);


    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                   
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center justify-center sm:justify-start">
                            <FaReceipt className="mr-3 text-indigo-600" /> Bill Lookup
                        </h1>

                        {/* Search Card */}
                        <div className="bg-white shadow-xl rounded-xl p-6 mb-8 border-t-4 border-indigo-500">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                <FaSearch className="inline mr-2 text-indigo-500" /> Search Bills by User Detail
                            </h2>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    value={userDetails}
                                    onChange={handleInputChange}
                                    onKeyPress={handleEnterKeyPress}
                                    placeholder="Enter User ID, Rental ID, or Email..."
                                    className="border border-gray-300 rounded-lg py-2 px-4 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                />
                                <button
                                    onClick={handleSearchClick}
                                    disabled={loading || !userDetails.trim()}
                                    className="w-full sm:w-auto inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md disabled:bg-indigo-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    {loading ? (
                                        <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                                    ) : (
                                        <>
                                            <FaSearch className="mr-2" /> Fetch Bills
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Results Card */}
                        <div className="bg-white shadow-xl rounded-xl p-6">
                            <div className="flex items-center justify-between mb-6 border-b pb-3">
                                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                    <FaMoneyBillWave className="mr-2 text-green-600" /> Bill Summary
                                </h2>
                                <span className="text-sm font-medium text-gray-500">
                                    Report Date: {new Date().toLocaleDateString()}
                                </span>
                            </div>
                            
                            {/* Content based on loading and data */}
                            {loading && (
                                <div className="flex justify-center items-center py-10">
                                    <Oval color="#4f46e5" secondaryColor="#6366f1" height={50} width={50} ariaLabel="loading-spinner" />
                                </div>
                            )}

                            {!loading && bills && (
                                <div className="space-y-6">
                                    {/* Individual Bills Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {billItems.map((item) => (
                                            <div key={item.label} className={`${item.bg} p-4 rounded-lg shadow-md border-l-4 border-l-${item.color.slice(5)}`}>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-semibold text-gray-600">{item.label}</span>
                                                    <item.icon className={`text-xl ${item.color}`} />
                                                </div>
                                                <span className={`block text-2xl font-extrabold ${item.color} mt-1`}>
                                                    {formatCurrency(item.value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Total Amount Card */}
                                    <div className="bg-indigo-500 p-5 rounded-lg shadow-lg text-white">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-semibold flex items-center">
                                                <FaCalculator className="mr-2"/> TOTAL AMOUNT DUE
                                            </h3>
                                            <span className="text-3xl font-extrabold">
                                                {totalBillAmount}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!loading && !bills && userDetails && (
                                <p className="text-center text-gray-500 py-8">
                                    Enter user details above and press "Fetch Bills" to see a summary.
                                    <br/>
                                    No bill data available for the entered details.
                                </p>
                            )}

                             {!loading && !bills && !userDetails && (
                                <p className="text-center text-gray-500 py-8">
                                    Enter user details (ID, Rental ID, or Email) in the search bar above to fetch and view their bill summary.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default BillsPage;